import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  acquisitionsApi,
  type AcqRequestDetail,
  type DuplicateCheckResult,
} from "@/lib/api/acquisitions";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { statusVariantMap } from "./AcquisitionRequestsPage";
import { RequestBibliography } from "./RequestBibliography";
import { BusyButton } from "@/components/acquisitions/BusyButton";
import { MoneyField } from "@/components/acquisitions/MoneyField";
import {
  Loader2,
  Send,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Eye,
  Clock,
  FileText,
  Package,
  Pencil,
  Trash2,
  AlertTriangle,
  UserPlus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { useAuth } from "@/contexts/AuthContext";
import { hasAnyPermission } from "@/lib/permissions";
import type { Fund, Vendor } from "@/lib/api/acquisitions";

const timelineIcons: Record<string, React.ReactNode> = {
  REQUEST_CREATED: <FileText className="h-4 w-4 text-slate-500" />,
  REQUEST_SUBMITTED: <Send className="h-4 w-4 text-blue-500" />,
  REVIEW_STARTED: <Eye className="h-4 w-4 text-purple-500" />,
  INFO_REQUESTED: <HelpCircle className="h-4 w-4 text-amber-500" />,
  APPROVED: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  REJECTED: <XCircle className="h-4 w-4 text-red-500" />,
  PO_CREATED: <Package className="h-4 w-4 text-blue-500" />,
  PATRON_REQUEST_ENRICHED: <UserPlus className="h-4 w-4 text-emerald-600" />,
};

export default function AcquisitionRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertStep, setConvertStep] = useState(1);
  const [convertFunds, setConvertFunds] = useState<Fund[]>([]);
  const [convertVendors, setConvertVendors] = useState<Vendor[]>([]);
  const [convertForm, setConvertForm] = useState({
    fundId: "",
    estimatedCost: "",
    quantity: "1",
    preferredVendorId: "",
    note: "",
  });
  const [request, setRequest] = useState<AcqRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoQuestion, setInfoQuestion] = useState("");
  const [dupCheck, setDupCheck] = useState<DuplicateCheckResult | null>(null);

  const loadRequest = async () => {
    if (!id) return;
    setLoading(true);
    try {
      setRequest(await acquisitionsApi.requests.getById(id));
    } catch {
      toast.error("Failed to load request.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
  }, [id]);

  useEffect(() => {
    if (!convertOpen) return;
    acquisitionsApi.funds
      .list({ isActive: true, limit: 200 })
      .then((r) => setConvertFunds(r.funds))
      .catch(() => setConvertFunds([]));
    acquisitionsApi.vendors
      .list({ isActive: true, limit: 200 })
      .then((r) => setConvertVendors(r.vendors))
      .catch(() => setConvertVendors([]));
  }, [convertOpen]);

  useEffect(() => {
    if (!request || (request.status !== "SUBMITTED" && request.status !== "UNDER_REVIEW")) {
      setDupCheck(null);
      return;
    }
    const isbn = request.isbn?.trim();
    const title = request.title?.trim();
    if (!isbn && !title) {
      setDupCheck(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await acquisitionsApi.requests.checkDuplicates(
          isbn || undefined,
          title || undefined
        );
        if (cancelled) return;
        const otherRequests = r.openRequests.filter((o) => o.id !== request.id);
        const hasBiblios = r.existingBiblios.length > 0;
        const hasOtherRequests = otherRequests.length > 0;
        if (hasBiblios || hasOtherRequests) {
          setDupCheck({
            ...r,
            openRequests: otherRequests,
          });
        } else {
          setDupCheck(null);
        }
      } catch {
        if (!cancelled) setDupCheck(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request?.id, request?.status, request?.isbn, request?.title]);

  const handleAction = async (key: string, action: () => Promise<unknown>, successMsg: string) => {
    setPending(key);
    try {
      await action();
      toast.success(successMsg);
      loadRequest();
      return true;
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Action failed."));
      return false;
    } finally {
      setPending(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!request) {
    return <div className="py-20 text-center text-slate-500">Request not found</div>;
  }

  const convertSelectedFund = convertFunds.find((f) => f.id === convertForm.fundId);
  const convertSelectedVendor = convertVendors.find((v) => v.id === convertForm.preferredVendorId);

  const cfg = statusVariantMap[request.status] || {
    variant: "neutral" as const,
    label: request.status,
  };
  const isReviewable = request.status === "SUBMITTED" || request.status === "UNDER_REVIEW";
  const canSubmitAsRequester =
    (request.status === "DRAFT" || request.status === "NEEDS_INFO") &&
    user?.id === request.requesterId;
  const isDraftOwner = request.status === "DRAFT" && user?.id === request.requesterId;
  const canEditRequest =
    (request.status === "DRAFT" || request.status === "NEEDS_INFO") &&
    user?.id === request.requesterId;

  const canEnrichPatron =
    request.source === "PATRON" &&
    ["SUBMITTED", "UNDER_REVIEW", "NEEDS_INFO"].includes(request.status) &&
    user &&
    hasAnyPermission(user.permissions, ["acquisitions.approveRequest"]);

  const validateConvertStep = (step: number): boolean => {
    if (step === 1) {
      if (!convertForm.fundId) {
        toast.error("Select a fund.");
        return false;
      }
      return true;
    }
    if (step === 2) {
      const cost = parseFloat(convertForm.estimatedCost);
      const qty = parseInt(convertForm.quantity, 10);
      if (Number.isNaN(cost) || cost < 0) {
        toast.error("Enter a valid estimated cost.");
        return false;
      }
      if (!qty || qty < 1) {
        toast.error("Enter a valid quantity.");
        return false;
      }
      return true;
    }
    return true;
  };

  const submitConvert = async () => {
    if (!request || !validateConvertStep(1) || !validateConvertStep(2)) {
      return;
    }
    const cost = parseFloat(convertForm.estimatedCost);
    const qty = parseInt(convertForm.quantity, 10);
    setPending("convert");
    try {
      await acquisitionsApi.requests.convertPatron(request.id, {
        fundId: convertForm.fundId,
        estimatedCost: cost,
        quantity: qty,
        preferredVendorId: convertForm.preferredVendorId || undefined,
        note: convertForm.note.trim() || undefined,
      });
      toast.success("Patron request updated with procurement details.");
      setConvertOpen(false);
      setConvertStep(1);
      loadRequest();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not save."));
    } finally {
      setPending(null);
    }
  };

  const busy = pending !== null;

  return (
    <PageContainer width="wide">
      <PageHeader
        title={request.title}
        description={
          [request.subtitle, request.author ? `by ${request.author}` : null]
            .filter(Boolean)
            .join(" · ") || undefined
        }
        breadcrumbItems={[
          { label: "Requests", href: "/acquisitions/requests" },
          { label: request.title },
        ]}
        showSearch={false}
        titleSize="md"
      />

      {dupCheck && (
        <Alert variant="warning" className="items-start">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">Possible duplicates — review before deciding.</span>
            {dupCheck.existingBiblios.length > 0 && (
              <span className="block mt-1 text-sm">
                Already in catalog: {dupCheck.existingBiblios.map((b) => b.title).join("; ")}
              </span>
            )}
            {dupCheck.openRequests.length > 0 && (
              <span className="block mt-1 text-sm">
                Other open requests:{" "}
                {dupCheck.openRequests.map((r) => `${r.title} (${r.status})`).join("; ")}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Details card */}
          <div className="rounded-lg border bg-white p-6 space-y-4">
            <div className="flex items-center gap-3">
              <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>
              <StatusBadge variant={request.source === "PATRON" ? "info" : "neutral"}>
                {request.source === "PATRON" ? "Patron Request" : "Staff Request"}
              </StatusBadge>
              {request.autoApproved && <StatusBadge variant="purple">Auto-Approved</StatusBadge>}
            </div>

            <RequestBibliography request={request} />

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 text-sm">
              <div>
                <span className="text-slate-500">ISBN</span>
                <p className="font-medium">{request.isbn || "—"}</p>
              </div>
              <div>
                <span className="text-slate-500">Publisher</span>
                <p className="font-medium">{request.publisher || "—"}</p>
              </div>
              <div>
                <span className="text-slate-500">Edition</span>
                <p className="font-medium">{request.edition || "—"}</p>
              </div>
              <div>
                <span className="text-slate-500">Format</span>
                <p className="font-medium">{request.format || "—"}</p>
              </div>
              <div>
                <span className="text-slate-500">Quantity</span>
                <p className="font-medium">{request.quantity}</p>
              </div>
              <div>
                <span className="text-slate-500">Estimated Cost</span>
                <p className="font-medium">
                  {request.estimatedCost != null
                    ? `₦${Number(request.estimatedCost).toLocaleString()}`
                    : "—"}
                </p>
              </div>
              <div>
                <span className="text-slate-500">Fund</span>
                <p className="font-medium">{request.fund?.name || "—"}</p>
              </div>
              <div>
                <span className="text-slate-500">Preferred vendor</span>
                <p className="font-medium">{request.preferredVendor?.name || "—"}</p>
              </div>
              <div>
                <span className="text-slate-500">Urgency</span>
                <p className="font-medium">{request.urgency}</p>
              </div>
            </div>

            {request.justification && (
              <div>
                <span className="text-sm text-slate-500">Justification</span>
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">
                  {request.justification}
                </p>
              </div>
            )}

            {request.rejectionReason && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <span className="text-sm font-medium text-red-800">Rejection Reason</span>
                <p className="mt-1 text-sm text-red-700">{request.rejectionReason}</p>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="rounded-lg border bg-white p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Activity Timeline</h3>
            {request.timeline.length === 0 ? (
              <p className="text-sm text-slate-500">No events yet</p>
            ) : (
              <div className="relative space-y-0">
                {request.timeline.map((ev, idx) => (
                  <div key={ev.id} className="flex gap-3 pb-4">
                    <div className="relative flex flex-col items-center">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">
                        {timelineIcons[ev.event] || <Clock className="h-4 w-4 text-slate-400" />}
                      </div>
                      {idx < request.timeline.length - 1 && (
                        <div className="flex-1 w-px bg-slate-200 mt-1" />
                      )}
                    </div>
                    <div className="flex-1 pt-1">
                      <p className="text-sm font-medium text-slate-900">
                        {ev.event.replace(/_/g, " ")}
                      </p>
                      {ev.data && (
                        <div className="text-xs text-slate-600 mt-0.5">
                          {(ev.data as any).reason && (
                            <span>Reason: {(ev.data as any).reason}</span>
                          )}
                          {(ev.data as any).question && (
                            <span>Question: {(ev.data as any).question}</span>
                          )}
                          {(ev.data as any).note && <span>Note: {(ev.data as any).note}</span>}
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(ev.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions sidebar */}
        <div className="space-y-4">
          <div className="rounded-lg border bg-white p-4 space-y-3">
            <h3 className="font-semibold text-slate-900 text-sm">Actions</h3>

            {canEditRequest && (
              <>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate(`/acquisitions/requests/${request.id}/edit`)}
                  disabled={busy}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {request.status === "NEEDS_INFO" ? "Add the requested information" : "Edit draft"}
                </Button>
                {isDraftOwner && (
                  <BusyButton
                    className="w-full"
                    variant="destructive"
                    busy={pending === "delete"}
                    disabled={busy}
                    icon={<Trash2 className="mr-2 h-4 w-4" />}
                    onClick={async () => {
                      if (
                        !window.confirm("Delete this draft permanently? This cannot be undone.")
                      ) {
                        return;
                      }
                      setPending("delete");
                      try {
                        await acquisitionsApi.requests.delete(request.id);
                        toast.success("Draft deleted.");
                        navigate("/acquisitions/requests");
                      } catch (err: unknown) {
                        toast.error(getApiErrorMessage(err, "Could not delete draft."));
                      } finally {
                        setPending(null);
                      }
                    }}
                  >
                    Delete draft
                  </BusyButton>
                )}
              </>
            )}

            {canSubmitAsRequester && (
              <BusyButton
                className="w-full"
                busy={pending === "submit"}
                disabled={busy}
                icon={<Send className="mr-2 h-4 w-4" />}
                onClick={() =>
                  handleAction(
                    "submit",
                    () => acquisitionsApi.requests.submit(request.id),
                    "Request submitted for review"
                  )
                }
              >
                Submit for Review
              </BusyButton>
            )}

            {canEnrichPatron && (
              <Button
                className="w-full"
                variant="secondary"
                onClick={() => {
                  setConvertStep(1);
                  setConvertForm({
                    fundId: request.fundId || "",
                    estimatedCost:
                      request.estimatedCost != null ? String(request.estimatedCost) : "",
                    quantity: String(request.quantity),
                    preferredVendorId:
                      request.preferredVendor?.id || request.preferredVendorId || "",
                    note: "",
                  });
                  setConvertOpen(true);
                }}
                disabled={busy}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Add fund & vendor details
              </Button>
            )}

            {request.status === "SUBMITTED" && (
              <BusyButton
                className="w-full"
                variant="outline"
                busy={pending === "review"}
                disabled={busy}
                icon={<Eye className="mr-2 h-4 w-4" />}
                onClick={() =>
                  handleAction(
                    "review",
                    () => acquisitionsApi.requests.startReview(request.id),
                    "Review started"
                  )
                }
              >
                Start Review
              </BusyButton>
            )}

            {isReviewable && (
              <>
                <BusyButton
                  className="w-full"
                  busy={pending === "approve"}
                  disabled={busy}
                  icon={<CheckCircle2 className="mr-2 h-4 w-4" />}
                  onClick={() =>
                    handleAction(
                      "approve",
                      () => acquisitionsApi.requests.approve(request.id),
                      "Request approved"
                    )
                  }
                >
                  Approve
                </BusyButton>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => setInfoOpen(true)}
                  disabled={busy}
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Request Info
                </Button>
                <Button
                  className="w-full"
                  variant="destructive"
                  onClick={() => setRejectOpen(true)}
                  disabled={busy}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject
                </Button>
              </>
            )}

            {request.status === "APPROVED" && (
              <Button
                className="w-full"
                onClick={() =>
                  navigate(`/acquisitions/orders/new?requestId=${encodeURIComponent(request.id)}`)
                }
              >
                <Package className="mr-2 h-4 w-4" />
                Create purchase order
              </Button>
            )}
          </div>

          <div className="rounded-lg border bg-white p-4 space-y-2 text-sm">
            <h3 className="font-semibold text-slate-900 text-sm">Info</h3>
            <div className="flex justify-between">
              <span className="text-slate-500">Created</span>
              <span>{new Date(request.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Updated</span>
              <span>{new Date(request.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>

          {request.poLines.length > 0 && (
            <div className="rounded-lg border bg-white p-4 space-y-2">
              <h3 className="font-semibold text-slate-900 text-sm">Linked Orders</h3>
              {request.poLines.map((pl) => (
                <button
                  key={pl.id}
                  onClick={() =>
                    pl.purchaseOrder && navigate(`/acquisitions/orders/${pl.purchaseOrder.id}`)
                  }
                  className="block w-full text-left rounded border p-2 text-sm hover:bg-slate-50"
                >
                  <span className="font-mono text-primary">
                    {pl.purchaseOrder?.orderNumber || "—"}
                  </span>
                  <span className="ml-2 text-slate-500">
                    {pl.purchaseOrder?.status?.replace(/_/g, " ")}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (required)"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <BusyButton
              variant="destructive"
              busy={pending === "reject"}
              disabled={!rejectReason.trim() || busy}
              onClick={async () => {
                const saved = await handleAction(
                  "reject",
                  () => acquisitionsApi.requests.reject(request.id, rejectReason),
                  "Request rejected"
                );
                if (!saved) return;
                setRejectOpen(false);
                setRejectReason("");
              }}
            >
              Reject
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request More Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={infoQuestion}
              onChange={(e) => setInfoQuestion(e.target.value)}
              placeholder="What information do you need?"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInfoOpen(false)}>
              Cancel
            </Button>
            <BusyButton
              busy={pending === "info"}
              disabled={!infoQuestion.trim() || busy}
              onClick={async () => {
                const saved = await handleAction(
                  "info",
                  () => acquisitionsApi.requests.requestInfo(request.id, infoQuestion),
                  "Information requested"
                );
                if (!saved) return;
                setInfoOpen(false);
                setInfoQuestion("");
              }}
            >
              Send
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={convertOpen}
        onOpenChange={(open) => {
          setConvertOpen(open);
          if (!open) setConvertStep(1);
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Enrich patron request</DialogTitle>
            <p className="text-sm text-slate-600 font-normal pt-1">
              Step {convertStep} of 4 — link this request to a fund and procurement details before
              approval or ordering.
            </p>
          </DialogHeader>

          <div className="flex justify-center gap-2 py-2">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                  convertStep === n &&
                    "bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-2",
                  convertStep > n && "bg-emerald-600 text-white",
                  convertStep < n && "bg-slate-100 text-slate-500"
                )}
              >
                {convertStep > n ? <CheckCircle2 className="h-4 w-4" /> : n}
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-500 -mt-1 mb-2">
            {convertStep === 1 && "Fund"}
            {convertStep === 2 && "Cost & quantity"}
            {convertStep === 3 && "Vendor & note"}
            {convertStep === 4 && "Review"}
          </p>

          <div className="space-y-3 pt-1 max-h-[min(52vh,420px)] overflow-y-auto">
            {convertStep === 1 && (
              <div className="space-y-1.5">
                <Label required>Fund</Label>
                <Select
                  value={convertForm.fundId || "__none__"}
                  onValueChange={(v) =>
                    setConvertForm((p) => ({ ...p, fundId: v === "__none__" ? "" : v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select fund" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select fund…</SelectItem>
                    {convertFunds.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name} ({f.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Charges for this purchase will draw against this fiscal fund.
                </p>
              </div>
            )}

            {convertStep === 2 && (
              <div className="grid grid-cols-2 gap-3">
                <MoneyField
                  id="convert-estimated-cost"
                  label="Estimated cost"
                  required
                  value={convertForm.estimatedCost}
                  onChange={(estimatedCost) => setConvertForm((p) => ({ ...p, estimatedCost }))}
                />
                <div className="space-y-1.5">
                  <Label required>Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    value={convertForm.quantity}
                    onChange={(e) => setConvertForm((p) => ({ ...p, quantity: e.target.value }))}
                    placeholder="1"
                  />
                </div>
              </div>
            )}

            {convertStep === 3 && (
              <>
                <div className="space-y-1.5">
                  <Label>Preferred vendor (optional)</Label>
                  <Select
                    value={convertForm.preferredVendorId || "__none__"}
                    onValueChange={(v) =>
                      setConvertForm((p) => ({
                        ...p,
                        preferredVendorId: v === "__none__" ? "" : v,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {convertVendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name} ({v.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Note (optional)</Label>
                  <Textarea
                    rows={3}
                    value={convertForm.note}
                    onChange={(e) => setConvertForm((p) => ({ ...p, note: e.target.value }))}
                    placeholder="Internal note for reviewers — appended to justification for audit."
                  />
                </div>
              </>
            )}

            {convertStep === 4 && (
              <div className="rounded-lg border bg-slate-50/80 p-4 space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-slate-200/80 pb-2">
                  <span className="text-slate-500">Fund</span>
                  <span className="font-medium text-slate-900 text-right">
                    {convertSelectedFund?.name ?? "—"}
                    {convertSelectedFund && (
                      <span className="text-slate-500 font-normal">
                        {" "}
                        ({convertSelectedFund.code})
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-200/80 pb-2">
                  <span className="text-slate-500">Estimated cost</span>
                  <span className="font-medium text-slate-900">
                    ₦{parseFloat(convertForm.estimatedCost || "0").toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-200/80 pb-2">
                  <span className="text-slate-500">Quantity</span>
                  <span className="font-medium text-slate-900">{convertForm.quantity}</span>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-200/80 pb-2">
                  <span className="text-slate-500">Preferred vendor</span>
                  <span className="font-medium text-slate-900 text-right">
                    {convertSelectedVendor?.name ?? "—"}
                  </span>
                </div>
                {convertForm.note.trim() ? (
                  <div>
                    <span className="text-slate-500 block mb-1">Note</span>
                    <p className="text-slate-800 whitespace-pre-wrap rounded border bg-white p-2">
                      {convertForm.note.trim()}
                    </p>
                  </div>
                ) : (
                  <p className="text-slate-500 italic">No internal note.</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              className="sm:mr-auto"
              onClick={() => setConvertOpen(false)}
            >
              Cancel
            </Button>
            <div className="flex gap-2 w-full sm:w-auto justify-end">
              {convertStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setConvertStep((s) => Math.max(1, s - 1))}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Back
                </Button>
              )}
              {convertStep < 4 ? (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (!validateConvertStep(convertStep)) return;
                    setConvertStep((s) => Math.min(4, s + 1));
                  }}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <BusyButton
                  type="button"
                  onClick={submitConvert}
                  busy={pending === "convert"}
                  disabled={busy}
                >
                  Confirm & save
                </BusyButton>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
